import { Commit, GetterTree, Module } from "vuex"

import { ProviderRoleEnum } from "@vitable/asclepius-api-client-ts"
import { useAsclepius } from "@/composables/api/asclepius"

export interface ProviderRoleInfo {
  role?: ProviderRoleEnum
  isLoading: boolean
}

export interface ProviderRolesState {
  providerData: Record<string, ProviderRoleInfo>
}

const state = (): ProviderRolesState => ({
  providerData: {}
})

const mutations = {
  SET_ROLE(state: ProviderRolesState, payload: { providerId: string; role: ProviderRoleEnum }) {
    if (!state.providerData[payload.providerId]) {
      state.providerData = {
        ...state.providerData,
        [payload.providerId]: { role: payload.role, isLoading: false }
      }
    }
    state.providerData[payload.providerId].role = payload.role
  },

  SET_LOADING(state: ProviderRolesState, payload: { providerId: string; isLoading: boolean }) {
    if (!state.providerData[payload.providerId]) {
      state.providerData = {
        ...state.providerData,
        [payload.providerId]: { isLoading: payload.isLoading }
      }
    }
    state.providerData[payload.providerId].isLoading = payload.isLoading
  },

  CLEAR_PROVIDER_ROLES(state: ProviderRolesState) {
    state.providerData = {}
  }
}

const actions = {
  async fetchProviderRole(
    { commit, state }: { commit: Commit; state: ProviderRolesState },
    providerId: string
  ): Promise<void> {
    const existing = state.providerData[providerId]
    if (existing?.role || existing?.isLoading) return

    const { getProviderApi, callWithResponse } = useAsclepius()
    commit("SET_LOADING", { providerId, isLoading: true })

    try {
      const provider = await callWithResponse(() => getProviderApi().getProvider({ providerId }))

      commit("SET_ROLE", {
        providerId,
        role: provider.user.provider?.role || ""
      })
    } catch (error) {
      console.error(`Failed to fetch provider role for ${providerId}:`, error)
    } finally {
      commit("SET_LOADING", { providerId, isLoading: false })
    }
  },
  clearProviderRoles({ commit }: { commit: Commit }): void {
    commit("CLEAR_PROVIDER_ROLES")
  }
}

const getters: GetterTree<ProviderRolesState, any> = {
  getRole:
    (state: ProviderRolesState) =>
    (providerId: string): ProviderRoleInfo | undefined => {
      const roleInfo = state.providerData[providerId]
      return roleInfo
    }
}

const providerRoles: Module<ProviderRolesState, any> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}

export default providerRoles
